import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { api } from "~/shared/services/api/fetch-client.ts";

const TrailerSizeSchema = z.object({
	code: z.string(),
	displayName: z.string(),
});

const TrailerSizesResponseSchema = z.array(TrailerSizeSchema);

export type TrailerSize = z.infer<typeof TrailerSizeSchema>;

async function fetchTrailerSizes(trailerCode: string): Promise<TrailerSize[]> {
	const response = await api.get(`trailer-sizes?trailerCode=${trailerCode}`);
	if (!response.ok) {
		throw new Error(`Failed to fetch trailer sizes: ${response.statusText}`);
	}
	const data = await response.json();

	return TrailerSizesResponseSchema.parse(data);
}

export function useTrailerSizes(trailerCode: string) {
	const { data, isLoading, error } = useQuery({
		queryKey: ["trailer-sizes", trailerCode],
		queryFn: () => fetchTrailerSizes(trailerCode),
		enabled: !!trailerCode,
		staleTime: 5 * 60 * 1000, // 5 minutes - static data stays fresh longer
		gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer for static data
		retry: 2,
	});

	return {
		trailerSizesData: data,
		isLoading,
		error,
	};
}
